import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { requestsApi, usersApi } from '../lib/api';
import clsx from 'clsx';

const statusOptions = [
  { value: '', label: '전체 상태' },
  { value: 'OPEN', label: '대기' },
  { value: 'IN_PROGRESS', label: '진행중' },
  { value: 'REVIEW', label: '검토' },
  { value: 'DONE', label: '완료' },
  { value: 'CLOSED', label: '종료' },
];

const priorityOptions = [
  { value: '', label: '전체 우선순위' },
  { value: 'P1', label: 'P1 (긴급)' },
  { value: 'P2', label: 'P2 (중요)' },
  { value: 'P3', label: 'P3 (일반)' },
];

const typeOptions = [
  { value: '', label: '전체 유형' },
  { value: 'Bugfix', label: '버그수정' },
  { value: 'Enhancement', label: '개선' },
  { value: 'Feature', label: '신규기능' },
  { value: 'Support', label: '지원' },
  { value: 'Other', label: '기타' },
];

function PriorityBadge({ priority }: { priority: string }) {
  const colors: Record<string, string> = {
    P1: 'bg-red-100 text-red-700',
    P2: 'bg-yellow-100 text-yellow-700',
    P3: 'bg-green-100 text-green-700',
  };

  return (
    <span className={clsx('px-2 py-0.5 text-xs font-medium rounded-full', colors[priority])}>
      {priority}
    </span>
  );
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    OPEN: 'bg-gray-100 text-gray-700',
    IN_PROGRESS: 'bg-blue-100 text-blue-700',
    REVIEW: 'bg-purple-100 text-purple-700',
    DONE: 'bg-green-100 text-green-700',
    CLOSED: 'bg-gray-100 text-gray-500',
  };

  const labels: Record<string, string> = {
    OPEN: '대기',
    IN_PROGRESS: '진행중',
    REVIEW: '검토',
    DONE: '완료',
    CLOSED: '종료',
  };

  return (
    <span className={clsx('px-2 py-0.5 text-xs font-medium rounded-full', colors[status])}>
      {labels[status] || status}
    </span>
  );
}

function TypeBadge({ type }: { type: string }) {
  const labels: Record<string, string> = {
    Bugfix: '버그수정',
    Enhancement: '개선',
    Feature: '신규기능',
    Support: '지원',
    Other: '기타',
  };

  return (
    <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
      {labels[type] || type}
    </span>
  );
}

const pageSizeOptions = [10, 20, 50, 100];

export default function RequestListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [showAdvanced, setShowAdvanced] = useState(false);

  const page = parseInt(searchParams.get('page') || '1');
  const pageSize = parseInt(searchParams.get('pageSize') || '50');
  const status = searchParams.get('status') || '';
  const priority = searchParams.get('priority') || '';
  const type = searchParams.get('type') || '';
  const assigneeId = searchParams.get('assigneeId') || '';
  const dateFrom = searchParams.get('dateFrom') || '';
  const dateTo = searchParams.get('dateTo') || '';

  // 사용자 목록
  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.list(),
  });
  const users = usersData?.data || [];

  const { data, isLoading } = useQuery({
    queryKey: ['requests', { page, pageSize, status, priority, type, assigneeId, dateFrom, dateTo, search: searchParams.get('search') }],
    queryFn: () => {
      const params: Record<string, string> = { page: String(page), pageSize: String(pageSize) };
      if (status) params.status = status;
      if (priority) params.priority = priority;
      if (type) params.type = type;
      if (assigneeId) params.assigneeId = assigneeId;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      if (searchParams.get('search')) params.search = searchParams.get('search')!;
      return requestsApi.list(params);
    },
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const activeFilters = [status, priority, type, assigneeId, dateFrom, dateTo].filter(Boolean).length;

  const clearFilters = () => {
    setSearchParams({});
    setSearch('');
  };

  const requests = data?.data?.items || [];
  const totalPages = data?.data?.totalPages || 1;

  const updateFilter = (key: string, value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    updateFilter('search', search);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">요청 목록</h1>
        <Link to="/requests/new" className="btn-primary">
          새 요청
        </Link>
      </div>

      {/* 필터 */}
      <div className="card p-4">
        <div className="flex flex-wrap gap-4 items-center">
          <form onSubmit={handleSearch} className="flex-1 min-w-[200px]">
            <div className="flex gap-2">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="검색 (제목, 내용, REQ-ID)"
                className="input flex-1"
              />
              <button type="submit" className="btn-secondary">
                검색
              </button>
            </div>
          </form>

          <select
            value={status}
            onChange={(e) => updateFilter('status', e.target.value)}
            className="input w-auto"
          >
            {statusOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>

          <select
            value={priority}
            onChange={(e) => updateFilter('priority', e.target.value)}
            className="input w-auto"
          >
            {priorityOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>

          <select
            value={type}
            onChange={(e) => updateFilter('type', e.target.value)}
            className="input w-auto"
          >
            {typeOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>

          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className={clsx('btn-ghost text-sm', showAdvanced && 'text-primary-600')}
          >
            고급 검색 {showAdvanced ? '▲' : '▼'}
          </button>

          {activeFilters > 0 && (
            <button onClick={clearFilters} className="btn-ghost text-sm text-red-600">
              필터 초기화
            </button>
          )}
        </div>

        {/* 고급 필터 */}
        {showAdvanced && (
          <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">담당자</label>
              <select
                value={assigneeId}
                onChange={(e) => updateFilter('assigneeId', e.target.value)}
                className="input w-full"
              >
                <option value="">전체</option>
                {users.map((user: any) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">시작일</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => updateFilter('dateFrom', e.target.value)}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">종료일</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => updateFilter('dateTo', e.target.value)}
                className="input w-full"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={() => {
                  const today = new Date();
                  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
                  updateFilter('dateFrom', firstDay.toISOString().split('T')[0]);
                  updateFilter('dateTo', today.toISOString().split('T')[0]);
                }}
                className="btn-ghost text-sm"
              >
                이번 달
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 테이블 */}
      <div className="card">
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>REQ-ID</th>
                <th>제목</th>
                <th>고객</th>
                <th>우선순위</th>
                <th>유형</th>
                <th>상태</th>
                <th>소요시간</th>
                <th>등록일</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="text-center py-8 text-gray-500">
                    로딩중...
                  </td>
                </tr>
              ) : requests.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-8 text-gray-500">
                    요청이 없습니다
                  </td>
                </tr>
              ) : (
                requests.map((req: any) => {
                const isCommRequest = req.reqId?.endsWith('-000');
                return (
                  <tr key={req.id} className={isCommRequest ? 'bg-blue-50' : ''}>
                    <td>
                      <Link
                        to={`/requests/${req.id}`}
                        className={clsx(
                          'hover:underline font-medium',
                          isCommRequest ? 'text-blue-600' : 'text-primary-600'
                        )}
                      >
                        {isCommRequest ? '📋 ' : ''}{req.reqId}
                      </Link>
                    </td>
                    <td>
                      <Link
                        to={`/requests/${req.id}`}
                        className="hover:text-primary-600"
                      >
                        {req.title}
                      </Link>
                    </td>
                    <td className="text-gray-600">{req.client}</td>
                    <td>
                      {!isCommRequest && <PriorityBadge priority={req.priority} />}
                    </td>
                    <td>
                      <TypeBadge type={req.type} />
                    </td>
                    <td>
                      <StatusBadge status={req.status} />
                    </td>
                    <td className="text-gray-600 text-sm">
                      {req.actualHours ? `${req.actualHours.toFixed(1)}시간` : '-'}
                    </td>
                    <td className="text-gray-500 text-sm">
                      {new Date(req.createdAt).toLocaleDateString('ko-KR')}
                    </td>
                  </tr>
                );
              })
              )}
            </tbody>
          </table>
        </div>

        {/* 페이지네이션 */}
        <div className="p-4 border-t border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">페이지당</span>
            <select
              value={pageSize}
              onChange={(e) => {
                const newParams = new URLSearchParams(searchParams);
                newParams.set('pageSize', e.target.value);
                newParams.set('page', '1');
                setSearchParams(newParams);
              }}
              className="input w-auto py-1 text-sm"
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size}개
                </option>
              ))}
            </select>
            <span className="text-sm text-gray-500">
              총 {data?.data?.total || 0}건
            </span>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  const newParams = new URLSearchParams(searchParams);
                  newParams.set('page', '1');
                  setSearchParams(newParams);
                }}
                disabled={page <= 1}
                className="btn-ghost disabled:opacity-50 text-sm"
              >
                처음
              </button>
              <button
                onClick={() => {
                  const newParams = new URLSearchParams(searchParams);
                  newParams.set('page', String(Math.max(1, page - 1)));
                  setSearchParams(newParams);
                }}
                disabled={page <= 1}
                className="btn-ghost disabled:opacity-50"
              >
                이전
              </button>
              <span className="flex items-center px-4 text-sm text-gray-600">
                {page} / {totalPages}
              </span>
              <button
                onClick={() => {
                  const newParams = new URLSearchParams(searchParams);
                  newParams.set('page', String(Math.min(totalPages, page + 1)));
                  setSearchParams(newParams);
                }}
                disabled={page >= totalPages}
                className="btn-ghost disabled:opacity-50"
              >
                다음
              </button>
              <button
                onClick={() => {
                  const newParams = new URLSearchParams(searchParams);
                  newParams.set('page', String(totalPages));
                  setSearchParams(newParams);
                }}
                disabled={page >= totalPages}
                className="btn-ghost disabled:opacity-50 text-sm"
              >
                마지막
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
